import {
  DbotEvent,
  DecisionContext,
  DecisionTypeHandler,
  DbotEventDispatcher,
} from "@/dbot/common/commonDomain";
import {
  counterpartyOf,
  ownParentOf,
  Merger,
  MergerForm,
  SubsidiaryMergerCompanyRole,
} from "./mergerDomain";
import { DecisionType } from "@/common/decisions";

export function initNewForm(
  dispatch: DbotEventDispatcher,
  context: DecisionContext<Merger>,
  data?: Merger,
): MergerForm {
  return new MergerForm(dispatch, context, data);
}

export function applyForm(form: MergerForm): Merger {
  const role = form.companyRole.value;
  const ownCompany = form.ownCompany;
  const counterparty = form.selectedCompany
    ? { name: form.selectedCompany.name, orgNo: form.selectedCompany.organizationnumber }
    : undefined;
  const keepSnapshots =
    role === form.data.companyRole &&
    counterparty !== undefined &&
    counterparty.orgNo === counterpartyOf(form.data)?.orgNo;
  const ownParent = ownParentOf(form.data);
  const isParentSubsidiaryMerger = form.isParentSubsidiaryMerger;
  const base: Merger = {
    type: DecisionType.merger,
    mergerDate: form.mergerDate.toISOString(),
    mergerType: form.mergerType.value,
    companyRole: role,
    accountingEffectiveDate: form.accountingEffectiveDate.toISOString(),
    acquiringCompanyTakesOverManagement: form.managementTakeover.value,
    transferringCompanyHasIssuedFinancialInstruments: form.hasIssuedFinancialInstruments.value,
    financialInstrumentsDescription: form.financialInstrumentsDescription.localizedTexts,
    boardMembersOrCeoHaveSpecialRights: form.hasSpecialRights.value,
    specialRightsDescription: form.specialRightsDescription.localizedTexts,
    shareholderNoticeDeadlineWaived: isParentSubsidiaryMerger
      ? form.noticeDeadlineWaived.value
      : undefined,
    annualAccountsLocation: isParentSubsidiaryMerger
      ? form.annualAccountsLocation.value
      : undefined,
    capitalChanges: isParentSubsidiaryMerger
      ? []
      : form.capitalChanges.map((change) => change.toData()),
    invoiceRecipient: form.invoiceRecipient.value,
    invoiceRecipientCompany:
      form.isOtherInvoiceRecipient && form.selectedInvoiceCompany
        ? {
            name: form.selectedInvoiceCompany.name,
            orgNo: form.selectedInvoiceCompany.organizationnumber,
          }
        : undefined,
    counterpartProjectId: form.data.counterpartProjectId,
    counterpartDecisionId: form.data.counterpartDecisionId,
    hasMergerPlanDocument: form.data.hasMergerPlanDocument,
  };
  switch (role) {
    case SubsidiaryMergerCompanyRole.ACQUIRING:
      return {
        ...base,
        acquiringCompany: ownCompany,
        acquiringCompanyParent: ownParent,
        transferringCompany: counterparty,
        transferringCompanyAddress: keepSnapshots
          ? form.data.transferringCompanyAddress
          : undefined,
        transferringCompanyCity: keepSnapshots ? form.data.transferringCompanyCity : undefined,
        transferringCompanyParent: keepSnapshots ? form.data.transferringCompanyParent : undefined,
      };
    case SubsidiaryMergerCompanyRole.TRANSFERRING:
      return {
        ...base,
        transferringCompany: ownCompany,
        transferringCompanyParent: ownParent,
        acquiringCompany: counterparty,
        acquiringCompanyAddress: keepSnapshots ? form.data.acquiringCompanyAddress : undefined,
        acquiringCompanyCity: keepSnapshots ? form.data.acquiringCompanyCity : undefined,
        acquiringCompanyParent: keepSnapshots ? form.data.acquiringCompanyParent : undefined,
      };
    default:
      return base;
  }
}

export function validate(form: MergerForm) {
  return form.validate();
}

export function isValid(form: MergerForm) {
  return form.isValid;
}

export const mergerHandler: DecisionTypeHandler<DecisionType.merger, Merger, MergerForm> = {
  type: DecisionType.merger,
  initNewForm,
  isValid,
  validate,
  applyForm,
  handleEvent(form: MergerForm, event: DbotEvent) {
    form.handleEvent(event);
  },
};
